import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { CheckCircle, Package, Truck, Phone } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OrderSuccessPage = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrder();
  }, [orderId]);

  const fetchOrder = async () => {
    try {
      const response = await axios.get(`${API}/orders/${orderId}`);
      setOrder(response.data);
    } catch (error) {
      console.error('Error fetching order:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950">
        <Navbar />
        <div className="container mx-auto px-4 md:px-6 max-w-7xl py-20 text-center">
          <div className="inline-block w-12 h-12 border-4 border-lime-400 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-zinc-950">
        <Navbar />
        <div className="container mx-auto px-4 md:px-6 max-w-7xl py-20 text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Order Not Found</h1>
          <Link to="/" className="text-lime-400 hover:text-lime-300">Go Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950">
      <Navbar />

      <div className="container mx-auto px-4 md:px-6 max-w-4xl py-16">
        <div className="text-center mb-12">
          <div className="w-24 h-24 bg-lime-400 mx-auto mb-6 flex items-center justify-center" data-testid="success-icon">
            <CheckCircle className="w-16 h-16 text-black" />
          </div>
          <h1 className="text-4xl md:text-6xl font-bold tracking-tight uppercase font-heading mb-4">
            Order <span className="text-lime-400">Confirmed!</span>
          </h1>
          <p className="text-zinc-300 text-lg">
            Thank you for choosing GreenPure Biofuel. Your order is being prepared for same-day delivery.
          </p>
        </div>

        <div className="bg-zinc-900 border border-zinc-800 p-8 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <p className="text-xs uppercase tracking-wider text-zinc-400 mb-1 font-semibold">Order ID</p>
              <p className="text-white font-mono" data-testid="order-id">{order.id}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-zinc-400 mb-1 font-semibold">Status</p>
              <span className="bg-lime-400/10 text-lime-400 border border-lime-400/20 px-3 py-1 text-sm font-mono uppercase" data-testid="order-status">
                {order.status}
              </span>
            </div>
          </div>

          <div className="border-t border-zinc-800 pt-6">
            <h2 className="font-heading text-xl font-bold uppercase mb-4">Delivery Details</h2>
            <div className="space-y-2 text-sm">
              <p className="text-zinc-300">
                <strong className="text-white">Name:</strong> {order.customer_name}
              </p>
              <p className="text-zinc-300">
                <strong className="text-white">Phone:</strong> {order.customer_phone}
              </p>
              <p className="text-zinc-300">
                <strong className="text-white">Email:</strong> {order.customer_email}
              </p>
              <p className="text-zinc-300">
                <strong className="text-white">Address:</strong> {order.delivery_address}
              </p>
              <p className="text-zinc-300">
                <strong className="text-white">Pincode:</strong> {order.pincode}
              </p>
            </div>
          </div>

          <div className="border-t border-zinc-800 pt-6 mt-6">
            <h2 className="font-heading text-xl font-bold uppercase mb-4">Order Items</h2>
            <div className="space-y-3">
              {order.items.map((item, idx) => (
                <div key={idx} className="flex justify-between items-center py-2 border-b border-zinc-800">
                  <div>
                    <p className="text-white font-semibold">{item.name}</p>
                    <p className="text-zinc-400 text-sm">Quantity: {item.quantity}</p>
                  </div>
                  <p className="text-white font-semibold">₹{item.total}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="border-t border-zinc-800 pt-6 mt-6">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-zinc-400">Subtotal</span>
                <span className="text-white">₹{order.subtotal}</span>
              </div>
              {order.discount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-lime-400">Discount {order.discount_code && `(${order.discount_code})`}</span>
                  <span className="text-lime-400">-₹{order.discount}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-zinc-400">GST (5%)</span>
                <span className="text-white" data-testid="order-gst">₹{order.gst || 0}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-zinc-400">Delivery</span>
                <span className="text-lime-400">FREE</span>
              </div>
              <div className="flex justify-between items-center pt-3 border-t border-zinc-800">
                <span className="text-xl font-semibold uppercase">Total Paid</span>
                <span className="text-3xl font-bold text-lime-400" data-testid="order-total">₹{order.total}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-zinc-900 border border-zinc-800 p-6 text-center">
            <Package className="w-8 h-8 text-lime-400 mx-auto mb-3" />
            <h3 className="font-semibold text-white mb-1">Packaging</h3>
            <p className="text-zinc-400 text-sm">Your order is being packed</p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 p-6 text-center">
            <Truck className="w-8 h-8 text-lime-400 mx-auto mb-3" />
            <h3 className="font-semibold text-white mb-1">Delivery Today</h3>
            <p className="text-zinc-400 text-sm">Same-day delivery guaranteed</p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 p-6 text-center">
            <Phone className="w-8 h-8 text-lime-400 mx-auto mb-3\" />
            <h3 className="font-semibold text-white mb-1">Support</h3>
            <p className="text-zinc-400 text-sm">+91-8431598801</p>
          </div>
        </div>

        <div className="bg-lime-400/10 border border-lime-400/20 p-6 mb-8">
          <h3 className="font-heading text-lg font-bold uppercase mb-3 text-lime-400">What's Next?</h3>
          <ul className="space-y-2 text-sm text-zinc-300">
            <li>✅ You'll receive a confirmation email shortly</li>
            <li>✅ Our delivery team will call you before dispatch</li>
            <li>✅ Track your order via the link sent to your email</li>
            <li>✅ Need help? WhatsApp us at +91-9876543210</li>
          </ul>
        </div>

        <div className="flex gap-4 justify-center">
          <Link
            to="/"
            className="bg-lime-400 text-black hover:bg-lime-500 font-bold uppercase tracking-wider px-8 py-4 shadow-[4px_4px_0px_0px_#ffffff] transition-all hover:-translate-y-1"
            data-testid="go-home-btn"
          >
            Back to Home
          </Link>
          <Link
            to="/products"
            className="bg-transparent border-2 border-zinc-700 text-white hover:border-lime-400 hover:text-lime-400 font-bold uppercase tracking-wider px-8 py-4 transition-colors"
            data-testid="continue-shopping-btn"
          >
            Continue Shopping
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default OrderSuccessPage;
